/**
 * Catalogue des bâtiments — **données pures** (§4).
 *
 * Aucune logique ici : juste ce que le jeu propose. L'économie lit ce catalogue,
 * le rendu y prend les sprites, la boutique y prend l'ordre d'affichage.
 *
 * `type` et `skinId` sont séparés dès le départ (§4) : un skin cosmétique de V2 ne
 * changera que `skinId`, sans toucher à l'économie ni à la logique de placement.
 *
 * Progression : chaque bâtiment produit davantage mais coûte disproportionnellement
 * plus cher, si bien qu'aucun ne rend le précédent inutile. Le joueur continue
 * d'acheter des maisons longtemps après avoir débloqué le bureau — c'est ce qui donne
 * à la ville sa densité, plutôt qu'un alignement de tours.
 */

import { LANDMARKS } from './landmarks';
import type { IconName } from '@/ui/icons';

export interface BuildingDefinition {
  /** Identifiant stable, écrit dans la sauvegarde. Ne jamais le renommer. */
  readonly type: string;
  /** Pictogramme de boutique. Chaque bâtiment a le sien : à 20 px, la silhouette
   * est la seule chose que le joueur lit. */
  readonly icon: IconName;
  /**
   * Emprise au sol, en cases, côté d'un carré `footprint × footprint`.
   *
   * **Mesurée, pas devinée** : la largeur du losange d'une emprise n × m vaut
   * `(n + m) × 256`. Les bornes alpha donnent 1,9 pour la maison (donc 1 × 1) et 3,6
   * pour l'épicerie (donc 2 × 2). Sans ce champ, l'état de jeu ne réservait qu'UNE case
   * par bâtiment : une épicerie posée sur des maisons les recouvrait à l'écran alors
   * que le moteur les croyait toutes bien placées.
   *
   * On retient un carré, et on arrondit vers le haut : réserver une case de trop est
   * invisible, en réserver une de moins laisse deux bâtiments se chevaucher.
   */
  readonly footprint: number;
  /** Skin par défaut. Les cosmétiques de V2 changeront celui-ci, jamais `type`. */
  readonly skinId: string;
  /**
   * Sprite rendu depuis le pack 3D Synty (`npm run render:sprites`), relatif à
   * `public/assets/buildings/`.
   *
   * **Deux contrats de cadrage**, selon la famille — voir `scripts/render-sprites/` :
   *
   * - Tuiles et décors : la base tombe au centre de l'image, ancre (0,5 · 0,5).
   * - Bâtiments : largeur = `footprint` cases exactement, et base posée à
   *   `footprint × TILE_HEIGHT × 0,75` du bord bas. Un bâtiment peut monter à sept
   *   hauteurs de losange : le cadrage symétrique aurait demandé des textures au-delà de
   *   la limite des GPU mobiles. L'ancre se **calcule** (`buildingAnchorY`), elle n'est
   *   pas tabulée.
   */
  readonly rendered: string;
  /**
   * Nombre de modèles différents disponibles pour ce type, à palier égal.
   *
   * Douze chariots hot-dog rigoureusement identiques trahissent le procédural dès la
   * dixième pose. Le pack contient onze maisons : n'en montrer que trois était un
   * gâchis. Le choix est déterministe depuis la position (voir `variantFor`).
   */
  readonly variants: number;
  /** Coût du premier exemplaire, en pièces. */
  readonly baseCost: number;
  /** Production du premier exemplaire, en pièces par seconde. */
  readonly baseProduction: number;
  /**
   * Nombre d'exemplaires du bâtiment précédent requis pour le voir apparaître en
   * boutique. Une boutique qui montre tout dès la première seconde noie le débutant.
   */
  readonly unlockAfter: number;
}

/**
 * Dix bâtiments (§6). L'ordre du tableau est l'ordre de la boutique et l'ordre de
 * déblocage : chaque entrée se débloque en possédant assez d'exemplaires de la
 * précédente.
 */
export const BUILDINGS: readonly BuildingDefinition[] = [
  {
    type: 'house',
    variants: 3,
    rendered: 'house-t1.png',
    footprint: 1,
    icon: 'bHouse',
    skinId: 'house-1',
    baseCost: 15,
    baseProduction: 0.5,
    unlockAfter: 0,
  },
  {
    type: 'cottage',
    variants: 3,
    rendered: 'cottage-t1.png',
    footprint: 1,
    icon: 'bCottage',
    skinId: 'cottage-1',
    baseCost: 120,
    baseProduction: 3,
    unlockAfter: 5,
  },
  {
    type: 'streetStand',
    /*
      ⚠️ DEUX variantes, et non trois. Le troisième modèle des paliers 1 et 2 était
      `SM_Prop_Sign_Pizza_01` et `SM_Prop_Sign_Burger_01` — des PANNEAUX de signalisation,
      pas des échoppes. Le joueur voyait donc « parfois une pizza » posée sur sa parcelle
      (rapport de Benoit, 21 août).

      C'est la règle 14 dans sa forme la plus pure : le nom d'un asset ne décrit pas sa
      forme. `Sign_Pizza` dit « pizza », et ce qu'il dessine est un panneau.

      Aucun modèle d'échoppe n'est libre dans le pack pour les remplacer — mieux vaut deux
      bonnes variantes que trois dont une fausse.

      ⚠️ `variants` est par TYPE, pas par palier : le camion du palier 3 tombe avec les
      panneaux, alors qu'il était juste. C'est le prix d'un champ unique, et il est
      préférable à un champ par palier qu'il faudrait tenir à jour trois fois.
    */
    variants: 2,
    rendered: 'streetStand-t1.png',
    footprint: 1,
    icon: 'bStand',
    skinId: 'stand-1',
    baseCost: 1_100,
    baseProduction: 22,
    unlockAfter: 5,
  },
  {
    type: 'grocery',
    variants: 1,
    rendered: 'grocery-t1.png',
    footprint: 2,
    icon: 'bGrocery',
    skinId: 'grocery-1',
    baseCost: 12_000,
    baseProduction: 180,
    unlockAfter: 5,
  },
  {
    type: 'fishShop',
    variants: 1,
    rendered: 'fishShop-t1.png',
    footprint: 2,
    icon: 'bFish',
    skinId: 'fish-1',
    baseCost: 130_000,
    baseProduction: 1_500,
    unlockAfter: 5,
  },
  {
    type: 'restaurant',
    variants: 1,
    rendered: 'restaurant-t1.png',
    footprint: 2,
    icon: 'bRestaurant',
    skinId: 'restaurant-1',
    baseCost: 1_400_000,
    baseProduction: 13_000,
    unlockAfter: 5,
  },
  {
    type: 'doctor',
    variants: 1,
    rendered: 'doctor-t1.png',
    footprint: 2,
    icon: 'bDoctor',
    skinId: 'doctor-1',
    baseCost: 20_000_000,
    baseProduction: 130_000,
    unlockAfter: 5,
  },
  {
    type: 'cinema',
    variants: 1,
    rendered: 'cinema-t1.png',
    footprint: 2,
    icon: 'bCinema',
    skinId: 'cinema-1',
    baseCost: 330_000_000,
    baseProduction: 1_600_000,
    unlockAfter: 5,
  },
  {
    type: 'school',
    variants: 1,
    rendered: 'school-t1.png',
    footprint: 2,
    icon: 'bSchool',
    skinId: 'school-1',
    baseCost: 5_100_000_000,
    baseProduction: 22_000_000,
    unlockAfter: 5,
  },
  {
    type: 'workplace',
    variants: 1,
    rendered: 'workplace-t1.png',
    footprint: 2,
    icon: 'bOffice',
    skinId: 'workplace-1',
    baseCost: 75_000_000_000,
    baseProduction: 290_000_000,
    unlockAfter: 5,
  },
];

/*
  ── LES REPÈRES SONT DANS LA TABLE, PAS DANS LA CHAÎNE ──────────────────────

  Les points de repère (`config/landmarks.ts`, ROADMAP 2.3) s'achètent en gemmes et n'ont
  aucune place dans `BUILDINGS` : l'ordre de ce tableau EST l'ordre de déblocage, et un
  repère glissé dedans casserait la chaîne de la boutique en pièces.

  Mais le moteur de rendu ne dessine que ce qu'il sait retrouver par son type, et l'état
  de jeu lit ici l'emprise de tout ce qui est posé. Les repères entrent donc dans la
  TABLE DE RECHERCHE, sans entrer dans la chaîne. Un repère est un bâtiment ; il n'est
  simplement pas un bâtiment qu'on débloque.

  ⚠️ C'est aussi ICI que se vérifie la compatibilité de forme : `LandmarkDefinition`
  n'importe pas `BuildingDefinition` (l'import serait circulaire), donc si un champ venait
  à manquer, c'est cette ligne qui refuserait de compiler.
*/
const BY_TYPE = new Map<string, BuildingDefinition>(
  [...BUILDINGS, ...LANDMARKS].map((definition) => [definition.type, definition]),
);

export function buildingByType(type: string): BuildingDefinition | undefined {
  return BY_TYPE.get(type);
}

export function buildingIndex(type: string): number {
  return BUILDINGS.findIndex((definition) => definition.type === type);
}
